import { onBeforeUnmount, readonly, ref } from "vue";
import * as Sentry from "@sentry/vue";
import GladiaLiveSession, { MAX_RECORDING_MS, type TranscriptParts } from "@utils/gladiaLiveSession";
import { useSurveyStore } from "@stores/survey/survey";

export type VoiceTranscriptionError = "microphone-denied" | "connection-failed";

export default function useVoiceTranscription({ partialTranscripts = false } = {}) {
  const surveyStore = useSurveyStore();
  const isRecording = ref(false);
  const isStopping = ref(false);
  const isStarting = ref(false);
  const error = ref<VoiceTranscriptionError | null>(null);

  let session: GladiaLiveSession | null = null;
  let mediaStream: MediaStream | null = null;
  let audioContext: AudioContext | null = null;
  let maxDurationTimeout: ReturnType<typeof setTimeout> | null = null;

  const cleanup = () => {
    if (maxDurationTimeout) {
      clearTimeout(maxDurationTimeout);
      maxDurationTimeout = null;
    }
    session?.destroy();
    session = null;
    mediaStream?.getTracks().forEach((track) => track.stop());
    mediaStream = null;
    if (audioContext && audioContext.state !== "closed") audioContext.close();
    audioContext = null;
    isRecording.value = false;
    isStopping.value = false;
    isStarting.value = false;
  };

  const stop = async (): Promise<string> => {
    if (!session || !isRecording.value) return "";
    isRecording.value = false;
    isStopping.value = true;

    const transcript = await session.stop();
    cleanup();
    return transcript;
  };

  const start = async (onTranscript: (parts: TranscriptParts) => void) => {
    // getUserMedia is async, so isRecording only flips true once it resolves - guard
    // against a double click firing a second start() before that happens.
    if (isRecording.value || isStopping.value || isStarting.value) return;
    isStarting.value = true;
    error.value = null;

    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      error.value = "microphone-denied";
      isStarting.value = false;
      return;
    }

    try {
      audioContext = new AudioContext();
      session = new GladiaLiveSession({ chatToken: surveyStore.survey?.chat_token, partialTranscripts });
      session.onTranscript(onTranscript);
      isRecording.value = true;
      maxDurationTimeout = setTimeout(stop, MAX_RECORDING_MS);
      await session.start(mediaStream, audioContext);
    } catch (err) {
      cleanup();
      error.value = "connection-failed";
      Sentry.captureException(err);
    } finally {
      isStarting.value = false;
    }
  };

  const cancel = () => {
    cleanup();
  };

  onBeforeUnmount(cleanup);

  return {
    isRecording: readonly(isRecording),
    isStopping: readonly(isStopping),
    isStarting: readonly(isStarting),
    error: readonly(error),
    start,
    stop,
    cancel,
  };
}
